/* eslint-disable no-restricted-globals, no-unused-vars */
// ─────────────────────────────────────────────────────────────────────────
// SERGA // PERSONAL OS — SERGA SHELL
// A command console for editing the OS the same way you'd reprogram a system.
// Drill into a node, drop into the shell, and rewrite it:
//   set priority Medium · task add "…" · new project "…" · rm
//
// Hybrid persistence (optimistic + background sync):
//   • PROJECT / TASK / ARTIFACT / TRAVEL edits update the card instantly, then
//     fire the real dashboard API in the background. On failure the shell prints
//     an error line and re-pulls authoritative data from the server.
//   • FINANCE runway / custom-zone edits hit the mock localStorage store (no
//     backend yet) and are clearly marked as drafts.
// ─────────────────────────────────────────────────────────────────────────
import {
  DB, persist, reset, serialize, uniqueId, allNodeIds, allZoneIds,
  nodeByCardId, isLiveProject,
} from './store.js';
import { currentInvestPeriod, setMood, setJournal, addCapture, keepCapture,
  discardCapture, routeCapture, systemStatus } from './cards.js';
import { ProjectsService } from '../services/projectsService';
import { FinanceService } from '../services/financeService';
import { TravelService } from '../services/travelService';
import { FOCUS, VITALS, CAPTURE } from './data.js';

const esc = s => String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
const stripQuotes = s => s.replace(/^["'`](.*)["'`]$/s, '$1').trim();
const cap = s => s ? s[0].toUpperCase() + s.slice(1).toLowerCase() : s;

// card label ↔ API enum maps
const API_PRIORITY = { high: 'high', medium: 'medium', low: 'low' };
const API_STATUS = { active: 'active', paused: 'paused', completed: 'completed', archived: 'archived' };
const API_TASK_STATE = { 'To Do': 'todo', 'In Progress': 'in_progress', 'Done': 'done', 'Blocked': 'blocked' };

export function createShell(world, onChange, reload, tray, sonic) {
  // ── DOM ───────────────────────────────────────────────────────────────────
  const root = document.createElement('div');
  root.id = 'shell';
  root.innerHTML = `
    <div class="sh-head" id="sh-head">
      <span class="sh-led"></span>
      <span class="sh-title">SERGA SHELL</span>
      <span class="sh-where" id="sh-where">~</span>
      <span class="sh-tag">EDIT MODE</span>
      <button class="sh-x" id="sh-x" title="close (esc)">✕</button>
    </div>
    <div class="sh-log" id="sh-log"></div>
    <form class="sh-input" id="sh-form" autocomplete="off">
      <span class="sh-prompt" id="sh-prompt">serga:/ ▸</span>
      <input class="sh-line" id="sh-line" spellcheck="false" autocomplete="off" autocapitalize="off" />
    </form>`;
  document.body.appendChild(root);

  const logEl = root.querySelector('#sh-log');
  const inputEl = root.querySelector('#sh-line');
  const promptEl = root.querySelector('#sh-prompt');
  const whereEl = root.querySelector('#sh-where');

  // ── log helpers ─────────────────────────────────────────────────────────────
  function row(html, cls) {
    const d = document.createElement('div');
    d.className = 'sh-row' + (cls ? ' ' + cls : '');
    d.innerHTML = html;
    logEl.appendChild(d);
    logEl.scrollTop = logEl.scrollHeight;
    return d;
  }
  const out = (s) => row(esc(s));
  const ok = (s) => row('<span class="sh-ok">✓</span> ' + esc(s), 'ok');
  const err = (s) => row('<span class="sh-err">!</span> ' + esc(s), 'err');
  const dim = (s) => row(esc(s), 'dim');
  const echo = (s) => row('<span class="sh-pmt">' + esc(promptText()) + '</span> <span class="sh-cmd">' + esc(s) + '</span>', 'echo');

  // ── background API sync (optimistic): the card already updated; persist to
  //    the backend and, on failure, surface it + re-pull authoritative data. ──
  function bg(promise, label) {
    Promise.resolve(promise).catch(e => {
      err('⚠ ' + label + ' didn’t save to server: ' + (e && e.message ? e.message : e));
      if (reload) { dim('re-pulling from server…'); Promise.resolve(reload()).catch(() => {}); }
    });
  }

  // ── context / prompt ─────────────────────────────────────────────────────────
  function ctx() { return world.getContext(); }
  function zonesList() { return world.listing(); }
  function bareId(id) { return id.replace(/^(z|p|tr|fin)-/, ''); }
  function pathStr() {
    const c = ctx();
    if (c.level === 0) return '/';
    const z = zonesList().find(x => x.id === c.zoneId);
    let s = '/' + (z ? bareId(z.id) : '?');
    if (c.level === 2) { const it = z && z.items.find(x => x.id === c.itemId); s += '/' + (it ? bareId(it.id) : '?'); }
    return s;
  }
  function promptText() { return 'serga:' + pathStr() + ' ▸'; }
  function syncPrompt() { promptEl.textContent = promptText(); whereEl.textContent = pathStr(); }

  // the node the shell is currently "on" (item if drilled in, else the zone)
  function selected() {
    const c = ctx();
    if (c.level === 2) return nodeByCardId(c.itemId);
    if (c.level === 1) return nodeByCardId(c.zoneId);
    return null;
  }
  const projectId = n => n.data.id;
  const liveProject = n => n.kind === 'project' && isLiveProject(n.data);

  // ── resolve a name/index/id against a list of {id,label} ─────────────────────
  function resolve(arg, list) {
    if (!arg) return null;
    if (/^\d+$/.test(arg)) return list[+arg - 1] || null;
    const a = arg.toLowerCase();
    return list.find(x => bareId(x.id).toLowerCase() === a || x.id.toLowerCase() === a)
        || list.find(x => x.label.toLowerCase() === a)
        || list.find(x => x.label.toLowerCase().startsWith(a))
        || list.find(x => x.label.toLowerCase().includes(a)) || null;
  }

  // keep core zones' node counts / children honest before a structural rebuild
  function recount() {
    const zp = DB.ZONES.find(z => z.id === 'projects');
    if (zp) { zp.children = DB.PROJECTS.map(p => p.id); zp.sub = DB.PROJECTS.length + ' active'; }
    const zt = DB.ZONES.find(z => z.id === 'travel');
    if (zt) { zt.children = DB.TRAVEL.map(t => t.id); zt.sub = DB.TRAVEL.length + ' places'; }
    DB.EXTRA_ZONES.forEach(z => { z.children = (z.nodes || []).map(n => n.id); z.sub = (z.nodes || []).length + ' nodes'; });
  }
  // mock entities mirror to localStorage; live projects persist via the API.
  function commit({ structural } = {}) {
    recount();
    persist();
    if (structural) world.sync();
    onChange && onChange();
  }

  // ═══ COMMANDS ════════════════════════════════════════════════════════════════
  function run(raw) {
    const line = raw.trim();
    if (!line) return;
    echo(line);
    const parts = line.split(/\s+/);
    const cmd = parts[0].toLowerCase();
    const rest = line.slice(parts[0].length).trim();
    try {
      (DISPATCH[cmd] || cmdUnknown)(rest, parts.slice(1), cmd);
    } catch (e) { err('error: ' + e.message); }
    syncPrompt();
  }

  function cmdUnknown(_r, _a, cmd) { err(`unknown command "${cmd}" — type help`); }

  // ── navigation ───────────────────────────────────────────────────────────────
  function cmdLs() {
    const c = ctx();
    if (c.level === 0) {
      dim('ZONES');
      zonesList().forEach((z, i) => row(`<span class="sh-i">${i + 1}</span> <span class="sh-key">${esc(bareId(z.id))}</span>  ${esc(z.label)} <span class="dim">· ${z.items.length} nodes</span>`));
      dim('› open <zone>   to enter');
    } else if (c.level === 1) {
      const z = zonesList().find(x => x.id === c.zoneId);
      dim('NODES IN ' + (z ? z.label.toUpperCase() : '?'));
      (z ? z.items : []).forEach((it, i) => row(`<span class="sh-i">${i + 1}</span> <span class="sh-key">${esc(bareId(it.id))}</span>  ${esc(it.label)}`));
      dim('› open <node>   ·   new node "Title"');
    } else { cmdShow(); }
  }
  function cmdTree() {
    zonesList().forEach(z => {
      row(`<span class="sh-key">${esc(bareId(z.id))}</span> <span class="dim">${esc(z.label)}</span>`);
      z.items.forEach(it => row(`  <span class="dim">└</span> <span class="sh-key">${esc(bareId(it.id))}</span> ${esc(it.label)}`));
    });
  }
  function cmdOpen(rest) {
    if (!rest) return err('open <zone> | <zone>/<node> | <node>');
    const segs = rest.split('/').map(s => stripQuotes(s.trim())).filter(Boolean);
    const c = ctx();
    if (segs.length === 2) {
      const z = resolve(segs[0], zonesList());
      if (!z) return err('no zone "' + segs[0] + '"');
      const it = resolve(segs[1], z.items);
      if (!it) return err('no node "' + segs[1] + '" in ' + z.label);
      world.enterItemById(z.id, it.id); ok('→ ' + it.label); enteredHint(it.id, 520); return;
    }
    const arg = segs[0];
    if (c.level >= 1) {
      const zc = zonesList().find(x => x.id === c.zoneId);
      const it = zc && resolve(arg, zc.items);
      if (it) { world.gotoItem(it.id); ok('→ ' + it.label); enteredHint(it.id); return; }
    }
    const zh = resolve(arg, zonesList());
    if (zh) { world.gotoZone(zh.id, c.level === 2); ok('→ ' + zh.label); return; }
    const here = c.level === 1 ? (zonesList().find(x => x.id === c.zoneId)?.items.map(i => bareId(i.id)).join(', ')) : zonesList().map(z => bareId(z.id)).join(', ');
    err('no zone or node "' + arg + '" — try: ' + here);
  }
  function enteredHint(cardId, delay = 0) {
    setTimeout(() => { const n = nodeByCardId(cardId); if (n) dim(n.kind === 'invest' ? 'edit: holding add <sym> symbol <ysym> qty <n> · holding <n> qty|basis <v> · holding rm <n>' : n.kind === 'budget' ? 'edit: budget <cat> <amount> · tx <n> <cat>' : 'fields: ' + FIELDS[n.kind].join(' · ')); syncPrompt(); }, delay);
  }
  function cmdBack() { world.back(); ok('← back'); }
  function cmdHome() { world.gotoOverview(); ok('← overview'); }

  // ── show node ────────────────────────────────────────────────────────────────
  const FIELDS = {
    project: ['title', 'status', 'priority', 'due', 'tag', 'brief', 'next'],
    travel: ['title', 'destination', 'status', 'purpose', 'notes', 'start', 'end', 'flex', 'linked'],
    finance: ['headline', 'note'],
    invest: [], // live + read-only (edit holdings.json via Claude Code)
    budget: [], // live + read-only (import a bank CSV via Claude Code)
    zone: ['label', 'code', 'sub'],
  };
  const artLabel = a => (typeof a === 'string' ? a : (a && a.title) || '');
  function cmdShow() {
    const n = selected();
    if (!n) return dim('at root — open a zone, then a node');
    const d = n.data;
    dim((n.kind).toUpperCase() + '  ' + bareId(n.cardId) + (liveProject(n) || n.kind === 'invest' || n.kind === 'budget' ? '  · live' : (n.kind !== 'zone' ? '  · draft' : '')));
    const show = (k, v) => row(`<span class="sh-field">${k.padEnd(9)}</span> <span class="sh-val">${esc(v == null || v === '' ? '—' : v)}</span>`);
    if (n.kind === 'project') {
      show('title', d.title); show('status', d.status); show('priority', d.priority);
      show('due', d.due); show('tag', d.tag); show('brief', d.desc); show('next', d.next);
      row(`<span class="sh-field">tasks</span> <span class="sh-val">${(d.tasks || []).length} · ${(d.tasks || []).filter(t => t.s === 'Done').length} done</span>`);
      (d.tasks || []).forEach((t, i) => row(`   <span class="sh-i">${i + 1}</span> [${stateMark(t.s)}] ${esc(t.t)} <span class="dim">${esc(t.s)}</span>`));
      row(`<span class="sh-field">artifacts</span> <span class="sh-val">${(d.artifacts || []).length}</span>`);
      (d.artifacts || []).forEach((a, i) => row(`   <span class="sh-i">${i + 1}</span> ${esc(artLabel(a))}`));
    } else if (n.kind === 'travel') {
      const dt = d.dates || {};
      show('dest', d.destination); show('title', d.title); show('status', d.status);
      show('window', [dt.start || '?', dt.end || '?'].join(' → ') + (dt.flexible ? ' · flex' : ''));
      show('purpose', d.purpose); show('linked', d.linkedProject); show('notes', d.notes);
      const legs = d.transport || [], stays = d.accommodation || [], checks = d.checklist || [];
      row(`<span class="sh-field">transport</span> <span class="sh-val">${legs.length}</span>`);
      legs.forEach((l, i) => row(`   <span class="sh-i">${i + 1}</span> ${esc((l.mode || '').toUpperCase())} ${esc(l.from || '?')}→${esc(l.to || '?')} <span class="dim">${esc(l.date || '')} ${esc(l.status || '')}</span>`));
      row(`<span class="sh-field">stays</span> <span class="sh-val">${stays.length}</span>`);
      stays.forEach((s, i) => row(`   <span class="sh-i">${i + 1}</span> ${esc(s.name || 'TBD')} <span class="dim">${esc(s.city || '')} ${esc(s.status || '')}</span>`));
      row(`<span class="sh-field">checklist</span> <span class="sh-val">${checks.filter(c => c.done).length}/${checks.length} done</span>`);
      checks.forEach((c, i) => row(`   <span class="sh-i">${i + 1}</span> [${c.done ? '✓' : ' '}] ${esc(c.label)}`));
      dim('live · edits sync to travel/' + d.id + '.json');
    } else if (n.kind === 'finance') {
      show('headline', d.headline); show('note', d.note);
      row(`<span class="sh-field">metrics</span> <span class="sh-val">${(d.metrics || []).length}</span>`);
      (d.metrics || []).forEach((m, i) => row(`   <span class="sh-i">${i + 1}</span> ${esc(m.label)} = <span class="sh-val">${esc(m.value)}</span> <span class="dim">${esc(m.hint)}</span>`));
      row(`<span class="sh-field">threads</span> <span class="sh-val">${(d.threads || []).length}</span>`);
      (d.threads || []).forEach((t, i) => row(`   <span class="sh-i">${i + 1}</span> [${stateMark(t.s)}] ${esc(t.t)} <span class="dim">${esc(t.note || '')}</span>`));
    } else if (n.kind === 'invest') {
      const cur = d.currency || '€';
      const money = v => v == null ? '—' : cur + Number(v).toLocaleString('en-US');
      const signed = (o) => {
        if (!o) return '—';
        const abs = (o.abs >= 0 ? '+' : '−') + cur + Math.abs(Number(o.abs)).toLocaleString('en-US');
        return `${o.pct > 0 ? '+' : ''}${o.pct}%  (${abs})`;
      };
      show('total', money(d.total));
      show('as of', d.asof);
      const per = currentInvestPeriod();           // selected window (view-state, not d.period)
      const win = per && d.delta ? d.delta[per] : null;
      if (win) show(per, signed(win));
      if (d.totalPL) show('P/L', signed(d.totalPL));
      row(`<span class="sh-field">holdings</span> <span class="sh-val">${(d.holdings || []).length}</span>`);
      (d.holdings || []).forEach((h, i) => row(`   <span class="sh-i">${i + 1}</span> <span class="sh-key">${esc(h.sym)}</span> <span class="dim">${esc(h.name)}</span> = <span class="sh-val">${esc(money(h.value))}</span>`));
      dim('read-only · live portfolio — edit holdings.json via Claude Code');
    } else if (n.kind === 'budget') {
      const cur = d.currency || 'CZK';
      const money = v => (Math.round(Number(v) || 0)).toLocaleString('en-US') + ' ' + cur;
      const t = d.totals || { spent: 0, income: 0, net: 0 };
      show('month', d.month);
      show('spent', money(t.spent));
      show('income', money(t.income));
      show('net', (t.net >= 0 ? '+' : '−') + money(Math.abs(t.net)));
      const cats = (d.categories || []).filter(c => c.spent > 0).slice().sort((a, b) => b.spent - a.spent);
      row(`<span class="sh-field">categories</span> <span class="sh-val">${cats.length}</span>`);
      cats.forEach((c, i) => row(`   <span class="sh-i">${i + 1}</span> ${esc(c.label)} <span class="dim">${esc(c.group || '')}</span> = <span class="sh-val">${esc(money(c.spent))}</span>${c.budget > 0 ? ` <span class="dim">/ ${esc(money(c.budget))}</span>` : ''}`));
      dim('read-only · live budget — import a bank CSV via Claude Code');
    } else if (n.kind === 'zone') {
      show('label', d.label); show('code', d.code); show('sub', d.sub);
    }
  }
  const stateMark = s => s === 'Done' ? '✓' : s === 'In Progress' ? '~' : ' ';

  // ── set field ────────────────────────────────────────────────────────────────
  function cmdSet(rest) {
    const n = selected();
    if (!n) return err('nothing selected — open a node first');
    if (n.kind === 'invest') return err('use: holding add/rm · holding <n> qty|basis|symbol <v>  (type help)');
    if (n.kind === 'budget') return err('use: budget <cat> <amount> · tx <n> <cat>  (type help)');
    if (n.kind === 'travel') return setTravel(rest, n);
    const m = rest.match(/^(\S+)\s*([\s\S]*)$/);
    if (!m) return err('set <field> <value>');
    const field = m[1].toLowerCase();
    let val = stripQuotes(m[2].trim());
    const allowed = FIELDS[n.kind];
    if (!allowed.includes(field)) return err(`field "${field}" not on ${n.kind} — try: ${allowed.join(', ')}`);
    const alias = { brief: 'desc', notes: 'desc' };
    const key = alias[field] || field;
    const d = n.data;

    if (field === 'priority') {
      if (!['high', 'medium', 'low'].includes(val.toLowerCase())) return err('priority must be High / Medium / Low');
      d.priority = cap(val);
    } else if (field === 'status') {
      d.status = cap(val);
    } else if (field === 'due') {
      d.due = (/^(none|-|null|tbd|)$/i.test(val)) ? null : val;
    } else if (field === 'label' || field === 'code') {
      d[key] = field === 'label' ? val.toUpperCase() : val;
    } else {
      d[key] = val;
    }
    commit({ structural: false });
    world.refreshNode(n.cardId);
    ok(`${field} = ${d[key] == null ? '—' : d[key]}`);

    // live project field edits → dashboard API (PATCH /projects/{id})
    if (liveProject(n)) {
      const API = { title: 'title', desc: 'description', next: 'nextAction', tag: null,
        priority: () => ({ priority: API_PRIORITY[d.priority.toLowerCase()] }),
        status: () => ({ status: API_STATUS[d.status.toLowerCase()] || d.status.toLowerCase() }),
        due: () => ({ deadline: d.due }) };
      const route = API[field];
      if (route === null) { dim('(tag is display-only — not stored on the backend)'); return; }
      let payload;
      if (typeof route === 'function') payload = route();
      else if (route) payload = { [route]: d[key] };
      if (payload) bg(ProjectsService.updateProject(projectId(n), payload), 'project update');
    } else if (n.kind === 'project') {
      dim('(draft — this project lives in a mock zone)');
    }
  }

  // ── checklist: task / item / thread ──────────────────────────────────────────
  function cmdTask(rest, args) {
    const n = selected();
    if (!n) return err('open a node first');
    if (n.kind === 'travel') return travelItems(rest, args, n);
    if (n.kind === 'finance') return cmdThread(rest, args);
    if (n.kind !== 'project') return err('no tasks on ' + n.kind);
    const d = n.data; d.tasks = d.tasks || [];
    const live = liveProject(n);
    const sub = (args[0] || '').toLowerCase();
    if (sub === 'add') {
      const txt = stripQuotes(rest.slice(3).trim());
      if (!txt) return err('task add "text"');
      const task = { t: txt, s: 'To Do' };
      d.tasks.push(task); commit(); world.refreshNode(n.cardId); ok('task added (#' + d.tasks.length + ')');
      if (live) bg(ProjectsService.createTasks(projectId(n), [{ title: txt }]).then(ts => {
        if (ts && ts[0]) { task.id = ts[0].taskId; }
      }), 'task add');
    } else if (sub === 'rm' || sub === 'del') {
      const i = +args[1] - 1; if (!d.tasks[i]) return err('no task #' + args[1]);
      const g = d.tasks.splice(i, 1)[0]; commit(); world.refreshNode(n.cardId); ok('removed: ' + g.t);
      if (live && g.id) bg(ProjectsService.deleteTask(projectId(n), g.id), 'task delete');
    } else if (/^\d+$/.test(sub)) {
      const i = +sub - 1; if (!d.tasks[i]) return err('no task #' + sub);
      const st = (args[1] || '').toLowerCase();
      const map = { done: 'Done', prog: 'In Progress', progress: 'In Progress', todo: 'To Do', do: 'To Do' };
      if (!map[st]) return err('task ' + sub + ' done|prog|todo');
      d.tasks[i].s = map[st]; commit(); world.refreshNode(n.cardId); ok(`task #${sub} → ${map[st]}`);
      if (live && d.tasks[i].id) bg(ProjectsService.updateTask(projectId(n), d.tasks[i].id, { status: API_TASK_STATE[map[st]] }), 'task update');
    } else { cmdShow(); }
  }
  // ── Travel editing (live — writes via TravelService, local optimistic) ───────
  const TRIP_STATUSES = ['idea', 'planned', 'booked', 'active', 'done'];
  const LEG_STATUSES = ['idea', 'booked'];
  const TRANSPORT_MODES = ['flight', 'train', 'bus', 'car', 'ferry'];
  const tripId = n => n.data.id;
  // push the changed field(s) to the API; local n.data is already mutated
  function saveTrip(n, payload) {
    bg(TravelService.updateTrip(tripId(n), payload), 'trip update');
  }
  function afterTripEdit(n, msg, structural) {
    commit({ structural: !!structural });
    if (!structural) world.refreshNode(n.cardId);
    ok(msg);
  }

  function setTravel(rest, n) {
    const d = n.data;
    const m = rest.match(/^(\S+)\s*([\s\S]*)$/);
    if (!m) return err('set <field> <value> — try: ' + FIELDS.travel.join(', '));
    const field = m[1].toLowerCase();
    const val = stripQuotes(m[2].trim());
    if (!FIELDS.travel.includes(field)) return err(`field "${field}" not on travel — try: ${FIELDS.travel.join(', ')}`);
    d.dates = d.dates || {};
    let payload;
    if (field === 'status') {
      const s = val.toLowerCase();
      if (!TRIP_STATUSES.includes(s)) return err('status: ' + TRIP_STATUSES.join(' / '));
      d.status = s; payload = { status: s };
    } else if (field === 'start' || field === 'end') {
      const k = field === 'start' ? 'start' : 'end';
      d.dates[k] = val || null; payload = { dates: { [k]: d.dates[k] } };
    } else if (field === 'flex') {
      d.dates.flexible = /^(1|true|yes|on)$/i.test(val); payload = { dates: { flexible: d.dates.flexible } };
    } else if (field === 'linked') {
      if (/^(none|-|null|)$/i.test(val)) { delete d.linkedProject; payload = { linkedProject: null }; }
      else { d.linkedProject = val; payload = { linkedProject: val }; }
    } else {
      const key = field === 'purpose' ? 'purpose' : field === 'notes' ? 'notes' : field; // title/destination/purpose/notes
      d[key] = val; payload = { [key]: val };
    }
    afterTripEdit(n, `${field} = ${val || '—'}`, field === 'status');
    saveTrip(n, payload);
  }

  // checklist: `task add "label"` · `task <n> done|todo` · `task rm <n>`
  function travelItems(rest, args, n) {
    const d = n.data; d.checklist = d.checklist || [];
    const sub = (args[0] || '').toLowerCase();
    if (sub === 'add') {
      const t = stripQuotes(rest.slice(3).trim());
      if (!t) return err('task add "label"');
      d.checklist.push({ label: t, done: false });
      afterTripEdit(n, 'checklist item added (#' + d.checklist.length + ')');
    } else if (sub === 'rm') {
      const i = +args[1] - 1;
      if (!d.checklist[i]) return err('no item #' + args[1]);
      const g = d.checklist.splice(i, 1)[0];
      afterTripEdit(n, 'removed: ' + g.label);
    } else if (/^\d+$/.test(sub)) {
      const i = +sub - 1;
      if (!d.checklist[i]) return err('no item #' + sub);
      const st = (args[1] || '').toLowerCase();
      if (st !== 'done' && st !== 'todo') return err('task <n> done|todo');
      d.checklist[i].done = st === 'done';
      afterTripEdit(n, `item #${sub} → ${st === 'done' ? 'DONE' : 'TO DO'}`);
    } else { return cmdShow(); }
    saveTrip(n, { checklist: d.checklist });
  }

  // transport legs: `leg add <mode> <FROM> <TO> [date] [ref]` · `leg <n> <field> <v>` · `leg rm <n>`
  function cmdTransport(rest, args) {
    const n = selected();
    if (!n || n.kind !== 'travel') return err('open a trip first (open travel · open tr-<slug>)');
    const d = n.data; d.transport = d.transport || [];
    const sub = (args[0] || '').toLowerCase();
    if (sub === 'add') {
      const [, mode, from, to, date, ref] = args;
      if (!mode || !TRANSPORT_MODES.includes(mode.toLowerCase())) return err('mode: ' + TRANSPORT_MODES.join(' / '));
      d.transport.push({ mode: mode.toLowerCase(), from: (from || '').toUpperCase() || null, to: (to || '').toUpperCase() || null, date: date || null, ref: ref || null, status: 'idea' });
      afterTripEdit(n, 'leg added (#' + d.transport.length + ')');
    } else if (sub === 'rm') {
      const i = +args[1] - 1;
      if (!d.transport[i]) return err('no leg #' + args[1]);
      d.transport.splice(i, 1); afterTripEdit(n, 'leg removed');
    } else if (/^\d+$/.test(sub)) {
      const i = +sub - 1;
      if (!d.transport[i]) return err('no leg #' + sub);
      const f = (args[1] || '').toLowerCase(); const v = stripQuotes(args.slice(2).join(' '));
      if (!['mode', 'from', 'to', 'date', 'ref', 'status'].includes(f)) return err('leg <n> mode|from|to|date|ref|status <v>');
      if (f === 'mode' && !TRANSPORT_MODES.includes(v.toLowerCase())) return err('mode: ' + TRANSPORT_MODES.join(' / '));
      if (f === 'status' && !LEG_STATUSES.includes(v.toLowerCase())) return err('leg status: ' + LEG_STATUSES.join(' / '));
      d.transport[i][f] = (f === 'from' || f === 'to') ? v.toUpperCase() : (f === 'mode' || f === 'status') ? v.toLowerCase() : v;
      afterTripEdit(n, `leg #${sub} ${f} = ${v}`);
    } else { return cmdShow(); }
    saveTrip(n, { transport: d.transport });
  }

  // accommodation: `stay add <Name> <City> [in] [out]` · `stay <n> <field> <v>` · `stay rm <n>`
  function cmdStay(rest, args) {
    const n = selected();
    if (!n || n.kind !== 'travel') return err('open a trip first (open travel · open tr-<slug>)');
    const d = n.data; d.accommodation = d.accommodation || [];
    const sub = (args[0] || '').toLowerCase();
    if (sub === 'add') {
      const [, name, city, ci, co] = args;
      d.accommodation.push({ name: name || null, city: city || '', checkIn: ci || null, checkOut: co || null, ref: null, status: 'idea' });
      afterTripEdit(n, 'stay added (#' + d.accommodation.length + ')');
    } else if (sub === 'rm') {
      const i = +args[1] - 1;
      if (!d.accommodation[i]) return err('no stay #' + args[1]);
      d.accommodation.splice(i, 1); afterTripEdit(n, 'stay removed');
    } else if (/^\d+$/.test(sub)) {
      const i = +sub - 1;
      if (!d.accommodation[i]) return err('no stay #' + sub);
      const f = (args[1] || '').toLowerCase(); const v = stripQuotes(args.slice(2).join(' '));
      const map = { name: 'name', city: 'city', in: 'checkIn', out: 'checkOut', ref: 'ref', status: 'status' };
      if (!map[f]) return err('stay <n> name|city|in|out|ref|status <v>');
      if (f === 'status' && !LEG_STATUSES.includes(v.toLowerCase())) return err('stay status: ' + LEG_STATUSES.join(' / '));
      d.accommodation[i][map[f]] = f === 'status' ? v.toLowerCase() : v;
      afterTripEdit(n, `stay #${sub} ${f} = ${v}`);
    } else { return cmdShow(); }
    saveTrip(n, { accommodation: d.accommodation });
  }
  function cmdArt(rest, args) {
    const n = selected();
    if (!n || n.kind !== 'project') return err('artifacts live on projects');
    const d = n.data; d.artifacts = d.artifacts || [];
    const live = liveProject(n);
    const sub = (args[0] || '').toLowerCase();
    if (sub === 'add') {
      const t = stripQuotes(rest.slice(3).trim()); if (!t) return err('art add "name"');
      const art = { title: t }; d.artifacts.push(art); commit(); world.refreshNode(n.cardId); ok('artifact added');
      if (live) bg(ProjectsService.createArtifact(projectId(n), { title: t, type: 'text' }).then(a => {
        if (a) art.id = a.artifactId;
      }), 'artifact add');
    } else if (sub === 'rm') {
      const i = +args[1] - 1; if (!d.artifacts[i]) return err('no artifact #' + args[1]);
      const g = d.artifacts.splice(i, 1)[0]; commit(); world.refreshNode(n.cardId); ok('removed: ' + artLabel(g));
      if (live && g && g.id) bg(ProjectsService.deleteArtifact(projectId(n), g.id), 'artifact delete');
    } else cmdShow();
  }
  function cmdMetric(rest, args) {
    const n = selected();
    if (!n || n.kind !== 'finance') return err('metrics live on finances');
    const d = DB.FINANCE; d.metrics = d.metrics || [];
    const sub = (args[0] || '').toLowerCase();
    if (sub === 'add') {
      const body = stripQuotes(rest.slice(3).trim());
      const [label, value, hint] = body.split('|').map(s => s.trim());
      if (!label) return err('metric add Label | Value | hint');
      d.metrics.push({ label: label.toUpperCase(), value: value || '—', hint: hint || '' });
      commit(); world.refreshNode(n.cardId); ok('metric added (draft)');
    } else if (sub === 'rm') { const i = +args[1] - 1; if (!d.metrics[i]) return err('no metric #' + args[1]); d.metrics.splice(i, 1); commit(); world.refreshNode(n.cardId); ok('metric removed'); }
    else cmdShow();
  }
  function cmdThread(rest, args) {
    const n = selected();
    if (!n || n.kind !== 'finance') return err('threads live on finances');
    const d = DB.FINANCE; d.threads = d.threads || [];
    const sub = (args[0] || '').toLowerCase();
    if (sub === 'add') { const t = stripQuotes(rest.slice(3).trim()); if (!t) return err('thread add "text"'); d.threads.push({ t, s: 'To Do', note: '' }); commit(); world.refreshNode(n.cardId); ok('thread added (draft)'); }
    else if (sub === 'rm') { const i = +args[1] - 1; if (!d.threads[i]) return err('no thread #' + args[1]); d.threads.splice(i, 1); commit(); world.refreshNode(n.cardId); ok('thread removed'); }
    else if (/^\d+$/.test(sub)) { const i = +sub - 1; if (!d.threads[i]) return err('no thread #' + sub); const map = { done: 'Done', prog: 'In Progress', todo: 'To Do', pending: 'Pending' }; const st = (args[1] || '').toLowerCase(); if (!map[st]) return err('thread n done|prog|todo|pending'); d.threads[i].s = map[st]; commit(); world.refreshNode(n.cardId); ok(`thread #${sub} → ${map[st]}`); }
    else cmdShow();
  }

  // ── finance editing: holdings (live API → re-priced) ─────────────────────────
  let rawHoldings = null;
  async function loadHoldings() {
    if (!rawHoldings) rawHoldings = (await FinanceService.getHoldings()).positions || [];
    return rawHoldings;
  }
  function findHolding(arg) {
    if (/^\d+$/.test(arg)) return rawHoldings[+arg - 1];
    return rawHoldings.find(p => String(p.sym).toLowerCase() === String(arg).toLowerCase());
  }
  async function saveHoldings(label) {
    try {
      await FinanceService.putHoldings(rawHoldings);
      ok(label + ' — saved · re-pricing…');
      setTimeout(() => { if (reload) Promise.resolve(reload()).catch(() => {}); }, 6000);
    } catch (e) { err('save failed: ' + (e && e.message ? e.message : e)); rawHoldings = null; }
  }
  function cmdHolding(rest, args) {
    const n = selected();
    if (!n || n.kind !== 'invest') return err('open Finances → Investments first (open finances · open invest)');
    (async () => {
      await loadHoldings();
      const sub = (args[0] || '').toLowerCase();
      if (!sub || sub === 'ls' || sub === 'list') {
        dim('HOLDINGS (' + rawHoldings.length + ')');
        rawHoldings.forEach((p, i) => row(`<span class="sh-i">${i + 1}</span> <span class="sh-key">${esc(p.sym)}</span> <span class="dim">${esc(p.quoteSymbol || p.type || '')}</span> qty <span class="sh-val">${esc(p.qty ?? p.amount ?? '—')}</span>${p.costBasis != null ? ` <span class="dim">@ ${esc(p.costBasis)}</span>` : ''}`));
        dim('› holding add <sym> symbol <ysym> qty <n> [basis <n>] [cur <CUR>]  ·  holding <n> qty|basis|symbol <v>  ·  holding rm <n>');
        return;
      }
      if (sub === 'add') {
        // labeled pairs: holding add <sym> symbol <ysym> qty <n> [basis <n>] [cur <CUR>] [name "…"] [type <t>]
        const a = args.slice(1);
        const sym = a[0];
        if (!sym) return err('holding add <sym> symbol <ysym> qty <n> [basis <n>] [cur <CUR>]');
        const opts = {};
        for (let i = 1; i < a.length; i += 2) {
          if (a[i + 1] == null) return err('"' + a[i] + '" needs a value');
          opts[(a[i] || '').toLowerCase()] = a[i + 1];
        }
        const quoteSymbol = opts.symbol || opts.sym;
        if (!quoteSymbol || opts.qty == null) return err('need at least: holding add <sym> symbol <ysym> qty <n>');
        const qty = Number(opts.qty);
        if (!Number.isFinite(qty) || qty <= 0) return err('qty must be a positive number');
        const pos = {
          sym: sym.toUpperCase(), name: opts.name || sym, type: (opts.type || 'equity').toLowerCase(),
          quoteSymbol, quoteCurrency: (opts.cur || opts.currency || 'EUR').toUpperCase(), qty,
        };
        if (opts.basis != null) {
          const b = Number(opts.basis);
          if (!Number.isFinite(b) || b <= 0) return err('basis must be a positive number');
          pos.costBasis = b;
        }
        rawHoldings.push(pos);
        await saveHoldings('added ' + pos.sym);
        return;
      }
      if (sub === 'rm' || sub === 'del') {
        const p = findHolding(args[1] || ''); if (!p) return err('no holding "' + (args[1] || '') + '"');
        rawHoldings = rawHoldings.filter(x => x !== p);
        await saveHoldings('removed ' + p.sym);
        return;
      }
      const p = findHolding(sub); if (!p) return err('no holding "' + sub + '" — try: holding ls');
      const field = (args[1] || '').toLowerCase(); const val = args[2];
      if (!field || val == null) return err('holding ' + sub + ' qty|basis|symbol|cur <value>');
      if (field === 'qty') p.qty = Number(val);
      else if (field === 'basis') p.costBasis = Number(val);
      else if (field === 'symbol') p.quoteSymbol = val;
      else if (field === 'cur' || field === 'currency') p.quoteCurrency = String(val).toUpperCase();
      else return err('field must be qty | basis | symbol | cur');
      await saveHoldings(p.sym + ' ' + field + ' = ' + val);
    })();
  }

  // ── finance editing: budget targets + recategorize (live API) ────────────────
  function cmdBudget(rest, args) {
    const n = selected();
    if (!n || n.kind !== 'budget') return err('open Finances → Budget first (open finances · open budget)');
    const cat = args[0]; const amount = args[1];
    const cats = n.data.categories || [];
    if (!cat) {
      dim('CATEGORY TARGETS · ' + n.data.month);
      cats.forEach((c, i) => row(`<span class="sh-i">${i + 1}</span> <span class="sh-key">${esc(c.id)}</span> ${esc(c.label)} <span class="dim">budget</span> <span class="sh-val">${esc(c.budget || 0)}</span> <span class="dim">spent ${esc(c.spent || 0)}</span>`));
      dim('› budget <categoryId> <amount>');
      return;
    }
    if (amount == null || isNaN(Number(amount))) return err('budget <categoryId> <amount>');
    const defined = cats.filter(c => c.group !== 'Uncategorized');
    if (!defined.find(c => c.id === cat)) return err('no category "' + cat + '" — try: budget');
    const doc = defined.map(c => ({ id: c.id, label: c.label, group: c.group, monthlyBudget: c.id === cat ? Number(amount) : (c.budget || 0) }));
    (async () => {
      try { await FinanceService.putBudgetCategories(doc); ok('budget ' + cat + ' = ' + amount); if (reload) reload(); }
      catch (e) { err('save failed: ' + (e && e.message ? e.message : e)); }
    })();
  }
  function cmdTx(rest, args) {
    const n = selected();
    if (!n || n.kind !== 'budget') return err('open Finances → Budget first');
    const txns = n.data.transactions || [];
    if (!args[0] || args[0].toLowerCase() === 'ls') {
      dim('TRANSACTIONS · ' + n.data.month + ' (' + txns.length + ')');
      txns.forEach((t, i) => row(`<span class="sh-i">${i + 1}</span> <span class="dim">${esc(t.date)}</span> ${esc(t.desc)} <span class="sh-val">${esc(t.amount)}</span> <span class="dim">[${esc(t.category)}]</span>`));
      dim('› tx <n> <categoryId>   to recategorize');
      return;
    }
    const idx = +args[0] - 1; const cat = args[1];
    if (!txns[idx]) return err('no transaction #' + args[0] + ' (1–' + txns.length + ')');
    if (!cat) return err('tx <n> <categoryId>');
    const updated = txns.map((t, i) => i === idx ? { ...t, category: cat } : t);
    (async () => {
      try { await FinanceService.putBudgetMonth(n.data.month, { currency: n.data.currency, transactions: updated }); ok('tx #' + args[0] + ' → ' + cat); if (reload) reload(); }
      catch (e) { err('save failed: ' + (e && e.message ? e.message : e)); }
    })();
  }

  // ── create ───────────────────────────────────────────────────────────────────
  function cmdNew(rest, args) {
    const kind = (args[0] || '').toLowerCase();
    const body = rest.slice((args[0] || '').length).trim();
    const title = stripQuotes(body);
    if (kind === 'project' || kind === 'node') return newProject(title);
    if (kind === 'place' || kind === 'trip') return newTrip(rest, args);
    if (kind === 'zone' || kind === 'area') return newZone(title);
    if (kind === 'task' || kind === 'item') return cmdTask('add ' + title, ['add', title]);
    if (kind === 'art' || kind === 'artifact') return cmdArt('add ' + title, ['add', title]);
    if (kind === 'metric') return cmdMetric('add ' + title, ['add']);
    if (kind === 'thread') return cmdThread('add ' + title, ['add', title]);
    err('new project|place|zone|task|art "Title"');
  }
  function newProject(title) {
    if (!title) return err('new project "Title"');
    const c = ctx();
    const inExtra = c.level >= 1 && DB.EXTRA_ZONES.find(z => 'z-' + z.id === c.zoneId);
    const id = uniqueId(title, allNodeIds());
    const node = { id, title, status: 'Active', priority: 'Medium', due: null, tag: 'NEW', desc: '', next: '—', tasks: [], artifacts: [] };
    let zoneCardId = 'z-projects';
    if (inExtra) { inExtra.nodes = inExtra.nodes || []; inExtra.nodes.push(node); zoneCardId = 'z-' + inExtra.id; }
    else DB.PROJECTS.push(node);
    commit({ structural: true });
    ok('created project "' + title + '"  →  ' + id);
    if (inExtra) {
      dim('(draft — created in a mock zone)');
      setTimeout(() => { world.enterItemById(zoneCardId, 'p-' + id); syncPrompt(); }, 60);
    } else {
      // real projects zone → create on the backend, then re-pull authoritative data
      bg(ProjectsService.createProject({ title, priority: 'medium', status: 'active' })
        .then(() => { dim('synced — re-pulling…'); return reload && reload(); }), 'project create');
    }
  }
  // new trip "Destination" [YYYY-MM] — creates server-side, then re-pulls.
  function newTrip(rest, args) {
    const body = rest.slice((args[0] || '').length).trim();
    // optional trailing YYYY-MM month token after the quoted destination
    const mMonth = body.match(/\s(\d{4}-\d{2})\s*$/);
    const month = mMonth ? mMonth[1] : null;
    const dest = stripQuotes(month ? body.slice(0, mMonth.index).trim() : body);
    if (!dest) return err('new trip "Destination" [YYYY-MM]');
    const input = { destination: dest, title: dest, status: 'idea' };
    if (month) input.dates = { start: `${month}-01` };
    ok('creating trip "' + dest + '"…');
    bg(TravelService.createTrip(input)
      .then((trip) => {
        dim('synced — travel/' + trip.id + '.json');
        return reload && reload();
      })
      .then(() => setTimeout(() => { world.enterItemById('z-travel', 'tr-' + slugFor(dest, month)); syncPrompt(); }, 80)),
      'trip create');
  }
  // best-effort local guess of the server slug (<dest-slug>-<YYYY-MM>) for nav
  function slugFor(dest, month) {
    const base = dest.toLowerCase().normalize('NFKD').replace(/[^\w\s-]/g, '').trim().replace(/[\s_-]+/g, '-');
    return month ? `${base}-${month}` : base;
  }
  function newZone(label) {
    if (!label) return err('new zone "LABEL"');
    const id = uniqueId(label, allZoneIds());
    const n = DB.ZONES.length + DB.EXTRA_ZONES.length + 1;
    DB.EXTRA_ZONES.push({ id, label: label.toUpperCase(), code: 'ZN-' + String(n).padStart(2, '0'), sub: '0 nodes', children: [], nodes: [] });
    commit({ structural: true });
    world.gotoOverview();
    ok('created zone "' + label.toUpperCase() + '"  →  ' + id + '  (draft)');
    dim('› open ' + id + '   then   new node "Title"');
  }

  // ── delete ───────────────────────────────────────────────────────────────────
  function cmdRm(rest) {
    let target = selected();
    if (rest) {
      const c = ctx();
      const list = c.level === 0 ? zonesList() : (zonesList().find(x => x.id === c.zoneId)?.items || []);
      const hit = resolve(stripQuotes(rest), list);
      if (hit) target = nodeByCardId(hit.id);
    }
    if (!target) return err('rm <name> — or open a node and just type rm');
    const wasLive = target.kind === 'project' && isLiveProject(target.data);
    const isTrip = target.kind === 'travel';
    const realId = target.data && target.data.id;
    const r = removeByCardId(target.cardId);
    if (r === 'protected') return err('that node is part of the core OS — can\'t delete it');
    if (!r) return err('could not find that node');
    commit({ structural: true });
    ok('deleted ' + bareId(target.cardId));
    if (wasLive && realId) bg(ProjectsService.deleteProject(realId), 'project delete');
    if (isTrip && realId) bg(TravelService.deleteTrip(realId), 'trip delete');
  }
  function removeByCardId(id) {
    if (id.startsWith('p-')) {
      const k = id.slice(2);
      let i = DB.PROJECTS.findIndex(p => p.id === k); if (i >= 0) { DB.PROJECTS.splice(i, 1); return true; }
      for (const z of DB.EXTRA_ZONES) { const j = (z.nodes || []).findIndex(nn => nn.id === k); if (j >= 0) { z.nodes.splice(j, 1); return true; } }
      return false;
    }
    if (id.startsWith('tr-')) { const i = DB.TRAVEL.findIndex(t => t.id === id.slice(3)); if (i >= 0) { DB.TRAVEL.splice(i, 1); return true; } return false; }
    if (id.startsWith('fin')) return 'protected';
    if (id.startsWith('z-')) {
      const k = id.slice(2);
      if (['projects', 'finances', 'travel'].includes(k)) return 'protected';
      const i = DB.EXTRA_ZONES.findIndex(z => z.id === k); if (i >= 0) { DB.EXTRA_ZONES.splice(i, 1); return true; }
      return false;
    }
    return false;
  }

  // ── system ───────────────────────────────────────────────────────────────────
  function cmdReset(rest) {
    if (rest.trim().toLowerCase() !== 'confirm') { err('this wipes your local drafts (finance/travel/zones) and reloads.'); dim('› type:  reset confirm'); return; }
    reset(); ok('drafts cleared — reloading…'); setTimeout(() => location.reload(), 500);
  }
  function cmdSave() { persist(); ok('drafts saved to this browser'); }
  function cmdExport() {
    const json = serialize();
    const blob = new Blob(['// SERGA // PERSONAL OS — exported mock drafts (finance / travel / zones)\n\nexport const DRAFTS = ' + json + ';\n'], { type: 'text/javascript' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = 'serga-drafts.js'; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 4000);
    ok('exported serga-drafts.js  (' + json.length + ' chars)');
    dim('mock drafts only — projects/tasks/artifacts persist via the backend');
  }
  function cmdClear() { logEl.innerHTML = ''; }
  function cmdStatus() {
    dim('SERGA OS · CONTENT INDEX');
    row(`projects <span class="sh-val">${DB.PROJECTS.length}</span>  ·  travel <span class="sh-val">${DB.TRAVEL.length}</span>  ·  metrics <span class="sh-val">${(DB.FINANCE.metrics || []).length}</span>  ·  zones <span class="sh-val">${DB.ZONES.length + DB.EXTRA_ZONES.length}</span>`);
    const tasks = DB.PROJECTS.reduce((a, p) => a + (p.tasks || []).length, 0);
    const done = DB.PROJECTS.reduce((a, p) => a + (p.tasks || []).filter(t => t.s === 'Done').length, 0);
    row(`tasks <span class="sh-val">${done}/${tasks} done</span>`);
  }

  function cmdHelp() {
    const H = (h) => dim(h);
    const C = (c, d) => row(`<span class="sh-key">${c.padEnd(22)}</span><span class="dim">${esc(d)}</span>`);
    H('NAVIGATE');
    C('ls / tree', 'list here / whole structure');
    C('open <name>', 'drill in (zone, node, or zone/node)');
    C('back · home', 'up one level · overview');
    C('show', 'print the current node\'s fields');
    H('EDIT  (open a node first)');
    C('set <field> <value>', 'e.g. set priority Medium · set due 2026-06-15');
    C('task add "…"', 'add a checklist item');
    C('task <n> done|prog|todo', 'set a task\'s state · task rm <n>');
    C('art add "…" · art rm <n>', 'project artifacts');
    C('metric add L | V | hint', 'finance snapshot · metric rm <n>');
    C('thread add "…"', 'finance threads · thread <n> <state>');
    H('TRAVEL  (open a trip first)');
    C('set status|start|end|flex|linked <v>', 'edit trip fields · set dest/title/purpose/notes');
    C('task add "…" · task <n> done|todo', 'checklist · task rm <n>');
    C('leg add <mode> <FROM> <TO> [date]', 'transport · leg <n> <field> <v> · leg rm <n>');
    C('stay add <Name> <City> [in] [out]', 'accommodation · stay <n> <field> <v> · stay rm <n>');
    C('new trip "Destination" [YYYY-MM]', 'create a trip');
    H('FINANCE  (open Investments / Budget)');
    C('holding add <sym> symbol <ysym> qty <n>', 'add a position (basis/cur optional) · holding <n> qty|basis|symbol <v>');
    C('budget <cat> <amount>', 'set a category monthly target');
    C('tx <n> <cat>', 'recategorize a transaction · tx ls to list');
    H('CREATE / DELETE');
    C('new project "Title"', 'also: new place / new zone');
    C('rm', 'delete current node (or rm <name>)');
    H('FEED');
    C('focus ls · focus show <id>', 'list Today\'s Focus · print an item incl. description');
    C('focus open|dismiss <id>', 'open its detail card · dismiss it');
    H('SONIC  (Spotify transport)');
    C('spotify now · play|pause|next|prev', 'now-playing · transport');
    C('spotify vol <n> · seek <m:ss>', 'volume · scrub');
    C('spotify shuffle|repeat [mode]', 'toggles · spotify devices · device <n>');
    C('spotify link|unlink|status', 'link a Spotify account · unlink · check');
    H('VITALS · CAPTURE · SYSTEM');
    C('mood <1-5> · journal "…"', 'log today\'s check-in + an optional line');
    C('capture "…" · capture list', 'drop a thought · list the inbox');
    C('capture keep|route|discard <n>', 'triage an item (route <n> <project>)');
    C('system', 'homelab node status (read-only)');
    H('SESSION');
    C('save · export', 'persist drafts · download drafts file');
    C('reset confirm', 'clear local drafts');
    C('clear · status · help', '');
    dim('projects/tasks/artifacts save to the backend; investments + budget are editable here; travel + finance investments/budget are live; runway/zones are local drafts.');
  }

  // ── FOCUS board (read-mostly; agent-written) ───────────────────────────────
  // `focus` / `focus ls` — list · `focus show <id>` — full item incl. description
  // `focus open <id>` — open its detail card · `focus dismiss <id>` — dismiss it
  function cmdFocus(rest, args) {
    const sub = (args[0] || 'ls').toLowerCase();
    const items = FOCUS.items || [];
    if (sub === 'ls' || sub === 'list') {
      if (!items.length) return dim('focus board empty');
      dim(`TODAY'S FOCUS · ${items.length} item${items.length === 1 ? '' : 's'}`);
      items.forEach(it => row(
        `<span class="sh-key">${esc(it.id)}</span>` +
        `<span class="dim">${esc((it.type || '').toUpperCase())} · ${esc(it.title)}${it.urgent ? ' · URGENT' : ''}</span>`));
      return;
    }
    const id = stripQuotes(args.slice(1).join(' '));
    if (!id) return err(`use: focus ${sub} <id>  (focus ls to list)`);
    const it = items.find(x => x.id === id);
    if (sub === 'show' || sub === 'cat') {
      if (!it) return err(`no focus item "${id}"`);
      dim((it.type || '').toUpperCase() + (it.urgent ? '  · URGENT' : ''));
      out(it.title);
      if (it.meta) dim(it.meta);
      out(it.description || '(no description)');
      if (it.src) dim('linked: ' + it.src);
      return;
    }
    if (sub === 'open') {
      if (!it) return err(`no focus item "${id}"`);
      const okOpen = tray && tray.focusOpen && tray.focusOpen(id);
      return okOpen ? ok('opened ' + id) : err('open the FEED rail first (item may be dismissed)');
    }
    if (sub === 'dismiss' || sub === 'rm') {
      const okRm = tray && tray.focusDismiss && tray.focusDismiss(id);
      return okRm ? ok('dismissed ' + id) : err(`no focus item "${id}"`);
    }
    return err('use: focus ls · focus show <id> · focus open <id> · focus dismiss <id>');
  }

  // ── SONIC / Spotify (global verb — the tray isn't a field node) ────────────
  const fmtMs = ms => { const t = Math.max(0, Math.round(ms / 1000)); return Math.floor(t / 60) + ':' + String(t % 60).padStart(2, '0'); };
  function cmdSpotify(rest, args) {
    const ctrl = sonic && sonic.controller;
    if (!ctrl) return err('SONIC unavailable');
    const sub = (args[0] || 'now').toLowerCase();
    const s = ctrl.state;
    switch (sub) {
      case 'now': case '': {
        const dev = (s.devices.find(d => d.id === s.deviceId) || s.devices.find(d => d.active));
        dim((s.connection || 'mock').toUpperCase() + (s.status === 'playing' ? ' · ▶' : ' · ❚❚'));
        out(`${s.track.title} — ${s.track.artist}`);
        dim(`${fmtMs(s.positionMs)} / ${fmtMs(s.track.durMs)}${dev ? ' · ' + dev.name : ''}`);
        return;
      }
      case 'play': ctrl.play(); return ok('▶ play');
      case 'pause': ctrl.pause(); return ok('❚❚ pause');
      case 'next': ctrl.next(); return ok('⏭ next');
      case 'prev': case 'previous': ctrl.prev(); return ok('⏮ prev');
      case 'shuffle': { const on = (args[1] || '').toLowerCase(); if (on === 'on' && !s.shuffle) ctrl.toggleShuffle(); else if (on === 'off' && s.shuffle) ctrl.toggleShuffle(); else if (!on) ctrl.toggleShuffle(); return ok('shuffle ' + (ctrl.state.shuffle ? 'on' : 'off')); }
      case 'repeat': { const m = (args[1] || '').toLowerCase(); const want = ['off', 'context', 'track'].includes(m) ? m : null; if (want) { let guard = 0; while (ctrl.state.repeat !== want && guard++ < 3) ctrl.cycleRepeat(); } else ctrl.cycleRepeat(); return ok('repeat ' + ctrl.state.repeat); }
      case 'vol': case 'volume': { const v = parseInt(args[1], 10); if (Number.isNaN(v)) return err('spotify vol <0-100>'); ctrl.setVolume(Math.max(0, Math.min(100, v))); return ok('volume ' + ctrl.state.volume); }
      case 'seek': { const m = (args[1] || '').match(/^(\d+):(\d{1,2})$/); if (!m) return err('spotify seek <m:ss>'); ctrl.seek((+m[1] * 60 + +m[2]) * 1000); return ok('seek ' + args[1]); }
      case 'devices': {
        if (!s.devices.length) return dim('no devices');
        dim('PLAYBACK DEVICES');
        s.devices.forEach((d, i) => row(`<span class="sh-i">${i + 1}</span> <span class="sh-key">${esc(d.name)}</span> <span class="dim">${esc(d.type)}${d.id === s.deviceId || d.active ? ' · active' : ''}</span>`));
        return;
      }
      case 'device': { const a = stripQuotes(args.slice(1).join(' ')); const d = /^\d+$/.test(a) ? s.devices[+a - 1] : s.devices.find(x => x.name.toLowerCase().includes(a.toLowerCase())); if (!d) return err('no such device (spotify devices)'); ctrl.setDevice(d.id); return ok('→ ' + d.name); }
      case 'link': case 'connect': ctrl.connect(); return ok('opening Spotify authorization…');
      case 'unlink': case 'disconnect': bg(Promise.resolve(ctrl.disconnect && ctrl.disconnect()), 'spotify unlink'); return ok('unlinking…');
      case 'status': bg(Promise.resolve(ctrl.refreshStatus && ctrl.refreshStatus()).then(st => dim(st ? `linked: ${!!st.linked}${st.premium != null ? ' · premium: ' + st.premium : ''}` : 'status unavailable')), 'spotify status'); return;
      case 'open': sonic.open && sonic.open(); return ok('SONIC opened');
      default: return err('spotify now|play|pause|next|prev|vol <n>|seek <m:ss>|shuffle|repeat|devices|device <n>|link|unlink|status');
    }
  }

  // ── VITALS · CAPTURE · SYSTEM (mutations reuse the card actions, which persist
  //    to the live API; the SHELL just confirms + re-renders the node) ───────────
  function cmdMood(rest, args) {
    const v = +args[0];
    if (!Number.isInteger(v) || v < 1 || v > 5) return err('mood <1-5> — log how today feels');
    setMood(v);
    world.refreshNode('vi-mind');
    const label = (VITALS.mind.labels && VITALS.mind.labels[v]) || '';
    ok(`mood logged: ${v}/5${label ? ' · ' + label : ''}`);
    dim('attach a line: journal "…"');
  }
  function cmdJournal(rest) {
    if (VITALS.mind.today == null) return err('log a mood first: mood <1-5>');
    const text = stripQuotes(rest.trim());
    setJournal(text);
    world.refreshNode('vi-mind');
    ok(text ? 'journal saved' : 'journal cleared');
  }
  function captureItem(arg) {
    if (!arg) return null;
    if (/^\d+$/.test(arg)) return CAPTURE.items[+arg - 1] || null;
    return CAPTURE.items.find(i => i.id === arg) || null;
  }
  const CAP_SUBS = ['list', 'ls', 'add', 'new', 'keep', 'discard', 'rm', 'route'];
  function cmdCapture(rest, args) {
    const sub = (args[0] || 'list').toLowerCase();
    // bare `capture "a thought"` (no subcommand) is a quick add
    if (args[0] && !CAP_SUBS.includes(sub)) {
      const t = stripQuotes(rest.trim());
      if (!t) return err('capture "text" · capture list · capture keep|route|discard <n>');
      addCapture(t); world.refreshNode('cap-inbox'); return ok('captured');
    }
    if (sub === 'list' || sub === 'ls') {
      if (!CAPTURE.items.length) return dim('inbox zero — nothing captured');
      CAPTURE.items.forEach((it, i) => row(`<span class="sh-i">${i + 1}</span> [${it.state === 'note' ? 'NOTE' : 'INBOX'}]${it.route ? ' ▸' + esc(it.route) : ''} ${esc(it.text)}`));
      return;
    }
    if (sub === 'add' || sub === 'new') {
      const t = stripQuotes(rest.slice(args[0].length).trim());
      if (!t) return err('capture add "your thought"');
      addCapture(t); world.refreshNode('cap-inbox'); return ok('captured');
    }
    const it = captureItem(args[1]);
    if (sub === 'keep') { if (!it) return err('capture keep <n>'); keepCapture(it.id); world.refreshNode('cap-inbox'); return ok('kept as note'); }
    if (sub === 'discard' || sub === 'rm') { if (!it) return err('capture discard <n>'); discardCapture(it.id); world.refreshNode('cap-inbox'); return ok('discarded'); }
    if (sub === 'route') {
      if (!it) return err('capture route <n> <project>');
      const proj = resolve(args[2], DB.PROJECTS.map(p => ({ id: p.id, label: p.tag || p.title })));
      if (!proj) return err('unknown project — try: ' + DB.PROJECTS.map(p => p.id).join(' / '));
      routeCapture(it.id, proj.id); world.refreshNode('cap-inbox'); return ok(`routed to ${proj.label}`);
    }
    return err('capture add "text" · capture list · capture keep|route|discard <n>');
  }
  function cmdSystem() {
    const nodes = systemStatus();
    if (!nodes.length) return dim('no nodes reporting');
    dim('SYSTEM · homelab');
    nodes.forEach(n => {
      const cls = n.status === 'UP' ? 'sh-ok' : n.status === 'DOWN' ? 'sh-err' : 'sh-val';
      row(`<span class="${cls}">●</span> <span class="sh-key">${esc(n.code || n.name)}</span> ${esc(n.name)} — <span class="sh-val">${n.status}</span> · ${n.up}/${n.total} svc${n.temp != null ? ' · ' + Math.round(n.temp) + '°C' : ''} · beat ${n.sinceBeat}s ago`);
    });
    dim('read-only · telemetry is machine-written by each Pi');
  }

  // ── dispatch table ───────────────────────────────────────────────────────────
  const DISPATCH = {
    ls: cmdLs, dir: cmdLs, tree: cmdTree,
    open: cmdOpen, cd: cmdOpen, enter: cmdOpen, go: cmdOpen,
    back: cmdBack, up: cmdBack, '..': cmdBack,
    home: cmdHome, root: cmdHome,
    show: cmdShow, cat: cmdShow, info: cmdShow, get: cmdShow,
    set: cmdSet, edit: cmdSet,
    task: cmdTask, tasks: cmdTask, item: cmdTask, check: cmdTask,
    art: cmdArt, artifact: cmdArt,
    metric: cmdMetric, metrics: cmdMetric,
    thread: cmdThread, threads: cmdThread,
    holding: cmdHolding, holdings: cmdHolding,
    budget: cmdBudget, tx: cmdTx,
    leg: cmdTransport, legs: cmdTransport, transport: cmdTransport, flight: cmdTransport,
    stay: cmdStay, stays: cmdStay, accommodation: cmdStay, hotel: cmdStay,
    new: cmdNew, add: cmdNew, mk: cmdNew, create: cmdNew,
    rm: cmdRm, del: cmdRm, delete: cmdRm,
    reset: cmdReset, save: cmdSave, export: cmdExport, dump: cmdExport,
    clear: cmdClear, cls: cmdClear, status: cmdStatus,
    focus: cmdFocus,
    spotify: cmdSpotify, sonic: cmdSpotify,
    mood: cmdMood, journal: cmdJournal,
    capture: cmdCapture, cap: cmdCapture, inbox: cmdCapture,
    system: cmdSystem, sys: cmdSystem, nodes: cmdSystem,
    help: cmdHelp, '?': cmdHelp, man: cmdHelp,
  };

  // ═══ input wiring ════════════════════════════════════════════════════════════
  const history = []; let hIdx = -1;
  const onSubmit = e => {
    e.preventDefault();
    const v = inputEl.value;
    if (v.trim()) { history.push(v); hIdx = history.length; }
    inputEl.value = '';
    run(v);
  };
  root.querySelector('#sh-form').addEventListener('submit', onSubmit);
  const onKey = e => {
    if (e.key === 'ArrowUp') { e.preventDefault(); if (history.length) { hIdx = Math.max(0, hIdx - 1); inputEl.value = history[hIdx] || ''; moveCaretEnd(); } }
    else if (e.key === 'ArrowDown') { e.preventDefault(); if (hIdx < history.length) { hIdx++; inputEl.value = history[hIdx] || ''; moveCaretEnd(); } }
    else if (e.key === 'Escape') { e.stopPropagation(); close(); }
  };
  inputEl.addEventListener('keydown', onKey);
  const moveCaretEnd = () => requestAnimationFrame(() => inputEl.setSelectionRange(inputEl.value.length, inputEl.value.length));

  // ── open / close + drag ───────────────────────────────────────────────────────
  let pollId = null;
  const btn = () => document.getElementById('shellbtn');
  function open() {
    root.classList.add('show'); document.body.classList.add('shell-open');
    btn() && btn().classList.add('armed');
    syncPrompt(); inputEl.focus();
    if (logEl.childElementCount === 0) greet();
    pollId = setInterval(syncPrompt, 700);
  }
  function close() { root.classList.remove('show'); document.body.classList.remove('shell-open'); btn() && btn().classList.remove('armed'); clearInterval(pollId); }
  function toggle() { root.classList.contains('show') ? close() : open(); }
  root.querySelector('#sh-x').addEventListener('click', close);

  function greet() {
    row('<span class="sh-banner">SERGA SHELL</span> <span class="dim">v1 · content editor</span>');
    dim('You\'re talking to the OS. Drill into any node, then rewrite it.');
    dim('Try:  ls  ·  open projects  ·  open czech  ·  set priority Medium');
    dim('Type  help  for the full command set.');
    dim('Projects/tasks/artifacts save to the backend; investments + budget are editable here; travel + finance investments/budget are live; runway/zones are local drafts.');
  }

  // drag by header
  const head = root.querySelector('#sh-head');
  let down = null;
  const onHeadDown = e => {
    if (e.target.id === 'sh-x') return;
    const r = root.getBoundingClientRect();
    down = { x: e.clientX, y: e.clientY, left: r.left, top: r.top };
    head.setPointerCapture(e.pointerId);
  };
  const onHeadMove = e => {
    if (!down) return;
    root.style.left = Math.max(8, Math.min(innerWidth - 120, down.left + e.clientX - down.x)) + 'px';
    root.style.top = Math.max(8, Math.min(innerHeight - 60, down.top + e.clientY - down.y)) + 'px';
    root.style.right = 'auto'; root.style.bottom = 'auto';
  };
  const onHeadUp = e => { down = null; head.releasePointerCapture(e.pointerId); };
  head.addEventListener('pointerdown', onHeadDown);
  head.addEventListener('pointermove', onHeadMove);
  head.addEventListener('pointerup', onHeadUp);

  function dispose() {
    clearInterval(pollId);
    if (root.parentNode) root.parentNode.removeChild(root);
    document.body.classList.remove('shell-open');
  }

  return { open, close, toggle, run, dispose };
}
